import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { Customer } from '../customer/customer.model';
import { CustomerService } from '../customer/customer.service';
import { ProductivityTask } from './productivity.model';
import { ProductivityService } from './productivity.service';

interface ResourceOption {
  id: number;
  resourceKey: string;
  label: string;
}

@Component({
  selector: 'app-productivity-tasks',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, ButtonModule, ChipModule, DatePickerModule, DialogModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule, TableModule, TagModule, TextareaModule, PageShellComponent, PageHeroComponent, PageDataPanelComponent, PageFormSectionComponent],
  template: `
    <app-page-shell>
      <app-page-hero
        eyebrow="Workspace"
        title="Tasks"
        subtitle="Track standalone work, incident resolution activity, ownership, and due dates.">
        <div nav class="task-filters">
          <p-select [options]="statusOptions" [(ngModel)]="statusFilter" optionLabel="label" optionValue="value" placeholder="Status" (onChange)="load()"></p-select>
          <p-button label="My Tasks" icon="pi pi-user" severity="secondary" [outlined]="true" (onClick)="loadMine()"></p-button>
          <p-button label="All" icon="pi pi-list" severity="secondary" [outlined]="true" (onClick)="assignedFilter = undefined; statusFilter = undefined; load()"></p-button>
        </div>
        <div actions>
          <p-button label="New Task" icon="pi pi-plus" (onClick)="openTask()"></p-button>
        </div>
      </app-page-hero>

      <app-page-data-panel title="Task Worklist" subtitle="Filter, update status, and manage assigned work." [resultText]="tasks.length + ' tasks'">
        <p-table [value]="tasks" [loading]="loading" [paginator]="true" [rows]="15" styleClass="workspace-table">
          <ng-template pTemplate="header">
            <tr><th>Task</th><th>Assignee</th><th>Due</th><th>Priority</th><th>Status</th><th class="text-right">Actions</th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-task>
            <tr>
              <td>
                <div class="font-semibold">{{ task.title }}</div>
                <div class="text-sm text-500">{{ task.description }}</div>
                <div class="text-xs text-500 mt-1" *ngIf="task.resourceKey">{{ task.resourceKey }} #{{ task.resourceId }}</div>
              </td>
              <td>
                <p-chip *ngIf="task.assignedCustomer || task.assignedCustomerId; else noAssignee"
                  icon="pi pi-user"
                  [label]="customerLabel(task.assignedCustomer, task.assignedCustomerId)"
                  styleClass="workspace-customer-chip">
                </p-chip>
                <ng-template #noAssignee><span class="text-500">-</span></ng-template>
              </td>
              <td>{{ task.dueDate | date:'mediumDate' }}</td>
              <td><p-tag [value]="task.priority || 'NORMAL'" [severity]="prioritySeverity(task.priority)"></p-tag></td>
              <td><p-select [options]="statusOptions" [(ngModel)]="task.status" optionLabel="label" optionValue="value" (onChange)="updateStatus(task, $event.value)"></p-select></td>
              <td class="text-right">
                <p-button icon="pi pi-pencil" [text]="true" (onClick)="openTask(task)"></p-button>
                <p-button icon="pi pi-trash" [text]="true" severity="danger" (onClick)="deleteTask(task)"></p-button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="6" class="text-center p-4 text-500">No tasks found.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>

      <p-dialog header="Task" [(visible)]="taskDialogVisible" [modal]="true" styleClass="workspace-dialog" [style]="{width: '48rem'}">
        <app-page-form-section icon="pi pi-check-square" title="Task Details" subtitle="Define ownership, priority, timing, and linked workspace context.">
          <div class="dialog-form-grid">
            <div class="dialog-field span-2">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-check-square"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="taskTitle" pInputText [(ngModel)]="draft.title">
                  <label for="taskTitle">Title</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field span-2">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <textarea id="taskDescription" pTextarea rows="3" [(ngModel)]="draft.description"></textarea>
                  <label for="taskDescription">Description</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="taskAssignee" [options]="customerOptions" [ngModel]="selectedAssignee"
                    (ngModelChange)="onAssigneeChange($event)" optionLabel="name" [filter]="true" [lazy]="true"
                    (onShow)="loadCustomerOptions()" (onLazyLoad)="loadCustomerOptions($event)" appendTo="body" styleClass="w-full">
                    <ng-template pTemplate="item" let-customer>
                      <div class="option-stack"><strong>{{ customer.name || ('Customer #' + customer.id) }}</strong><small>{{ customer.emailAddress || customer.phoneNumber || ('ID ' + customer.id) }}</small></div>
                    </ng-template>
                    <ng-template pTemplate="selectedItem" let-customer>
                      <span>{{ customer?.name || (customer?.id ? ('Customer #' + customer.id) : '') }}</span>
                    </ng-template>
                  </p-select>
                  <label for="taskAssignee">Assigned customer</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-datePicker inputId="taskDueDate" [(ngModel)]="draft.dueDate" appendTo="body" styleClass="w-full"></p-datePicker>
                  <label for="taskDueDate">Due date</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="taskPriority" [options]="priorityOptions" [(ngModel)]="draft.priority" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                  <label for="taskPriority">Priority</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-circle"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="taskStatus" [options]="statusOptions" [(ngModel)]="draft.status" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                  <label for="taskStatus">Status</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="taskResourceKey" [options]="resourceTypeOptions" [(ngModel)]="draft.resourceKey"
                    optionLabel="label" optionValue="value" [filter]="true" appendTo="body" styleClass="w-full"
                    (onChange)="onResourceKeyChange()"></p-select>
                  <label for="taskResourceKey">Resource type</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select inputId="taskResource" [options]="resourceOptions" [ngModel]="selectedResource"
                    (ngModelChange)="onResourceChange($event)" optionLabel="label" [filter]="true" [lazy]="true"
                    (onShow)="loadResourceOptions()" (onLazyLoad)="loadResourceOptions($event)" appendTo="body" styleClass="w-full"></p-select>
                  <label for="taskResource">Resource</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
          </div>
        </app-page-form-section>
        <ng-template pTemplate="footer">
          <p-button label="Cancel" severity="secondary" (onClick)="taskDialogVisible = false"></p-button>
          <p-button label="Save" icon="pi pi-check" (onClick)="saveTask()"></p-button>
        </ng-template>
      </p-dialog>
    </app-page-shell>
  `,
  styles: [`
    :host { display: block; }
    .task-filters { display: flex; flex-wrap: wrap; gap: 0.5rem; }
    :host ::ng-deep .workspace-table .p-datatable-table { min-width: 64rem; }
    .dialog-form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1rem;
    }
    .dialog-field {
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
      min-width: 0;
      color: #334155;
      font-weight: 600;
    }
    .dialog-field > span {
      font-size: 0.85rem;
    }
    .span-2 {
      grid-column: 1 / -1;
    }
    :host ::ng-deep .dialog-field .p-inputtext,
    :host ::ng-deep .dialog-field .p-inputnumber,
    :host ::ng-deep .dialog-field .p-inputnumber-input,
    :host ::ng-deep .dialog-field .p-datepicker,
    :host ::ng-deep .dialog-field .p-select,
    .dialog-field input,
    .dialog-field textarea {
      width: 100%;
    }
    @media (max-width: 640px) {
      .dialog-form-grid { grid-template-columns: 1fr; }
      .span-2 { grid-column: auto; }
    }
  `]
})
export class ProductivityTasksComponent implements OnInit {
  private service = inject(ProductivityService);
  private customerService = inject(CustomerService);
  tasks: ProductivityTask[] = [];
  draft: ProductivityTask = { title: '', priority: 'NORMAL', status: 'OPEN' };
  customerOptions: Customer[] = [];
  selectedAssignee: Customer | null = null;
  resourceOptions: ResourceOption[] = [];
  selectedResource: ResourceOption | null = null;
  loading = false;
  taskDialogVisible = false;
  statusFilter?: string;
  assignedFilter?: number;
  statusOptions = ['OPEN', 'IN_PROGRESS', 'BLOCKED', 'COMPLETED', 'CANCELLED', 'NOT_REQUIRED'].map(value => ({ label: value, value }));
  priorityOptions = ['LOW', 'NORMAL', 'HIGH', 'URGENT'].map(value => ({ label: value, value }));
  resourceTypeOptions = [
    { label: 'Shipment Delivery', value: 'SHIPMENT_DELIVERY' },
    { label: 'Customs Clearance', value: 'CUSTOMS_CLEARANCE' },
    { label: 'Trip Costing', value: 'TRIP_COSTING' },
    { label: 'Warehouse Transfer', value: 'WAREHOUSE_TRANSFER' },
    { label: 'Fleet Mobilization', value: 'FLEET_MOBILIZATION' },
    { label: 'Accounts Receivable', value: 'ACCOUNTS_RECEIVABLE' },
    { label: 'Productivity Summary', value: 'PRODUCTIVITY_SUMMARY' },
    { label: 'Productivity Incident', value: 'PRODUCTIVITY_INCIDENT' }
  ];

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.service.getTasks({ status: this.statusFilter, assignedCustomerId: this.assignedFilter }).subscribe({
      next: res => { this.tasks = res.data || []; this.loading = false; },
      error: () => this.loading = false
    });
  }

  loadMine(): void {
    this.assignedFilter = this.service.getCustomerId();
    this.load();
  }

  openTask(task?: ProductivityTask): void {
    this.draft = task ? { ...task } : { title: '', priority: 'NORMAL', status: 'OPEN', assignedCustomerId: this.service.getCustomerId() };
    this.syncDialogSelections();
    this.taskDialogVisible = true;
  }

  saveTask(): void {
    this.service.saveTask(this.draft).subscribe(() => {
      this.taskDialogVisible = false;
      this.service.refreshSummary();
      this.load();
    });
  }

  updateStatus(task: ProductivityTask, status: string): void {
    if (!task.id) return;
    this.service.updateTaskStatus(task.id, status, task.completionNotes).subscribe(() => this.service.refreshSummary());
  }

  deleteTask(task: ProductivityTask): void {
    if (!task.id || !confirm(`Delete task ${task.title}?`)) return;
    this.service.deleteTask(task.id).subscribe(() => {
      this.service.refreshSummary();
      this.load();
    });
  }

  prioritySeverity(priority?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    if (priority === 'URGENT') return 'danger';
    if (priority === 'HIGH') return 'warn';
    if (priority === 'LOW') return 'secondary';
    return 'info';
  }

  customerLabel(customer?: Customer, fallbackId?: number): string {
    return customer?.name || customer?.emailAddress || (fallbackId ? `Customer #${fallbackId}` : '-');
  }

  loadCustomerOptions(event?: any): void {
    this.customerService.getCustomersByCompany(this.service.getCompanyId()).subscribe(customers => {
      const search = String(event?.filter || '').toLowerCase();
      this.customerOptions = (customers || []).filter(customer => {
        const label = `${customer.name || ''} ${customer.emailAddress || ''} ${customer.phoneNumber || ''} ${customer.id || ''}`.toLowerCase();
        return !search || label.includes(search);
      });
      this.syncCustomerSelection();
    });
  }

  loadResourceOptions(event?: any): void {
    const search = String(event?.filter || '').toLowerCase();
    const options = new Map<string, ResourceOption>();
    const addOption = (resourceKey?: string, resourceId?: number) => {
      if (!resourceKey || !resourceId) return;
      const option: ResourceOption = {
        id: resourceId,
        resourceKey,
        label: `${this.resourceTypeLabel(resourceKey)} #${resourceId}`
      };
      options.set(`${resourceKey}:${resourceId}`, option);
    };

    this.tasks.forEach(task => addOption(task.resourceKey, task.resourceId));
    addOption(this.draft.resourceKey, this.draft.resourceId);

    this.resourceOptions = Array.from(options.values())
      .filter(option => (!this.draft.resourceKey || option.resourceKey === this.draft.resourceKey)
        && (!search || option.label.toLowerCase().includes(search)));
    this.syncResourceSelection();
  }

  onAssigneeChange(customer: Customer | null): void {
    this.selectedAssignee = customer;
    this.draft.assignedCustomerId = customer?.id;
  }

  onResourceKeyChange(): void {
    this.draft.resourceId = undefined;
    this.selectedResource = null;
    this.loadResourceOptions();
  }

  onResourceChange(resource: ResourceOption | null): void {
    this.selectedResource = resource;
    this.draft.resourceKey = resource?.resourceKey || this.draft.resourceKey;
    this.draft.resourceId = resource?.id;
  }

  private syncDialogSelections(): void {
    this.syncCustomerSelection();
    this.loadResourceOptions();
  }

  private syncCustomerSelection(): void {
    const id = this.draft.assignedCustomerId;
    this.selectedAssignee = id
      ? this.customerOptions.find(customer => customer.id === id) || ({ id, name: `Customer #${id}` } as Customer)
      : null;
  }

  private syncResourceSelection(): void {
    const key = this.draft.resourceKey;
    const id = this.draft.resourceId;
    this.selectedResource = key && id
      ? this.resourceOptions.find(option => option.resourceKey === key && option.id === id) || { id, resourceKey: key, label: `${this.resourceTypeLabel(key)} #${id}` }
      : null;
  }

  private resourceTypeLabel(resourceKey: string): string {
    return this.resourceTypeOptions.find(option => option.value === resourceKey)?.label || resourceKey.replace(/_/g, ' ');
  }
}
